import {InboundBridgeBucketRecord} from './index';
import {Box, Container, Header, Spinner} from '@amzn/awsui-components-react';
import React from 'react';
import ReactEcharts from 'echarts-for-react';
import {chartColorsLight} from '../common/colors';
import * as awsui from '@amzn/awsui-design-tokens/polaris';

export function InboundBridgeWaterfallChart({data, isLoading}: {data: InboundBridgeBucketRecord[]; isLoading: boolean}) {
    const renderData = transformData(data);

    if (isLoading) {
        return (
            <Container header={<Header variant="h2">Aggregated Plan-to-Plan Changes</Header>}>
                <Box textAlign="center" color="inherit">
                    <Spinner size="large" />
                </Box>
            </Container>
        );
    }

    return (
        <Container header={<Header variant="h2">Aggregated Plan-to-Plan Changes</Header>}>
            <ReactEcharts
                key={JSON.stringify(renderData)}
                option={getOption()}
                style={{height: '50vh'}}
                opts={{renderer: 'svg'}}
                data-testid="page-waterfall-chart"
            />
        </Container>
    );

    function getOption() {
        return {
            color: chartColorsLight,
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'shadow',
                },
            },
            legend: {
                show: false,
            },
            xAxis: {
                type: 'category',
                data: renderData.bucketNames,
                axisLabel: {interval: 0, rotate: 30},
            },
            yAxis: [
                {
                    type: 'value',
                },
                {
                    type: 'value',
                    show: false,
                    scale: true,
                },
            ],
            series: getSeries(renderData),
        };
    }
}

export function getSeries(data: ChartData) {
    return [
        {
            name: 'Base',
            type: 'bar',
            stack: 'total',
            yAxisIndex: 0,
            label: {
                show: true,
                position: 'inside',
                color: '#000000',
                formatter: (params) => {
                    const index = params.dataIndex;
                    const isFirstOrLast = index === 0 || index === data.valueSeries.length - 1;
                    return isFirstOrLast ? '' : data.deltaSeries[index].toLocaleString();
                },
            },
            tooltip: {
                show: false,
            },
            emphasis: {
                disabled: true,
            },
            data: data.baseSeries,
            itemStyle: {
                borderColor: 'transparent',
                color: 'transparent',
            },
        },
        {
            name: 'Stacked',
            type: 'bar',
            stack: 'total',
            yAxisIndex: 0,
            label: {
                show: true,
                position: 'inside',
                color: '#ffffff',
                formatter: (params) => {
                    const index = params.dataIndex;
                    const isFirstOrLast = index === 0 || index === data.valueSeries.length - 1;
                    return isFirstOrLast ? data.valueSeries[index].toLocaleString() : '';
                },
            },
            emphasis: {
                disabled: true,
            },
            tooltip: {
                show: false,
            },
            data: data.stackedSeries,
            itemStyle: {
                color: (params) => {
                    if (params.dataIndex === 0 || params.dataIndex === data.valueSeries.length - 1) {
                        return awsui.colorChartsBlue1300;
                    }
                    return data.deltaSeries[params.dataIndex] > 0 ? awsui.colorChartsTeal300 : awsui.colorChartsOrange300;
                },
                borderRadius: [4, 4, 4, 4],
            },
        },
        {
            name: 'Value',
            type: 'line',
            yAxisIndex: 1,
            label: {
                show: false,
            },
            lineStyle: {
                width: 0,
            },
            emphasis: {
                disabled: true,
            },
            data: data.valueSeries,
            itemStyle: {
                opacity: 0,
                color: awsui.colorChartsBlue1300,
            },
        },
        {
            name: 'Change',
            type: 'line',
            yAxisIndex: 1,
            label: {
                show: false,
            },
            lineStyle: {
                width: 0,
            },
            emphasis: {
                disabled: true,
            },
            data: data.deltaSeries,
            itemStyle: {
                opacity: 0,
                color: (params) => (params.value > 0 ? awsui.colorChartsTeal300 : awsui.colorChartsOrange300),
            },
        },
    ];
}

export function transformData(records: InboundBridgeBucketRecord[]): ChartData {
    if (records.length === 0) {
        return {bucketNames: [], baseSeries: [], stackedSeries: [], deltaSeries: [], valueSeries: []};
    }

    const sortedData = [...records].sort((a, b) => a.seqId - b.seqId);
    const withNewPlan = [
        ...sortedData,
        {
            ...sortedData[sortedData.length - 1],
            seqId: 99999,
            name: 'New Plan',
            value: sortedData[sortedData.length - 1].value,
        },
    ];

    const transformed = withNewPlan.map((record, index, array) => {
        if (index === 0) {
            return {
                ...record,
                base: 0,
                stacked: record.value,
                delta: record.value,
            };
        } else if (index === array.length - 1) {
            return {
                ...record,
                base: 0,
                stacked: record.value,
                delta: 0,
            };
        } else {
            const prevValue = array[index - 1]?.value ?? 0;
            const currentValue = record.value ?? 0;
            const delta = currentValue - prevValue;

            return {
                ...record,
                base: delta >= 0 ? prevValue : currentValue,
                stacked: Math.abs(delta),
                delta,
            };
        }
    });

    const bucketNames = transformed.map((record) => record.name!);
    const baseSeries = transformed.map((record) => record.base!);
    const stackedSeries = transformed.map((record) => record.stacked!);
    const deltaSeries = transformed.map((record) => record.delta!);
    const valueSeries = transformed.map((record) => record.value!);

    return {bucketNames, baseSeries, stackedSeries, deltaSeries, valueSeries};
}

export type ChartData = {
    bucketNames: string[];
    baseSeries: number[];
    stackedSeries: number[];
    deltaSeries: number[];
    valueSeries: number[];
};
